import React, { useState, useEffect } from 'react';
import useProfileCompletion from '../../../features/jobs/hooks/useProfileCompletion';
import Button from '../../../shared/components/ui/Button';
import useCategories from '../../../features/jobs/hooks/useCategories';
import useTowns from '../../../features/jobs/hooks/useTowns';
import {
  fetchSkillsByCategorySlug,
  uploadProfilePhoto,
  updateProfile,
  addUserSkill,
  removeUserSkill,
  fetchUserCv,
} from '../../../services/api';
import { getMediaUrl } from '../../../utils/mediaUrl';
import { CvModal } from '../../client/components/ClientApplicationJobCard';
import CardIcon from '../../../shared/components/icons/CardIcon';
import ProfilePhotoCropper from '../../../shared/components/profile/ProfilePhotoCropper';
import PhoneVerification from '../../../shared/components/profile/PhoneVerification';

const formatNationalId = (value = '') => {
  const digits = String(value).replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 6) return digits;
  if (digits.length <= 8) return `${digits.slice(0, 6)}/${digits.slice(6)}`;
  return `${digits.slice(0, 6)}/${digits.slice(6, 8)}/${digits.slice(8)}`;
};

function SectionHeading({ children, onEdit, icon }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name={icon || 'category'} />{children}</h2>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-xl leading-none text-slate-500 transition hover:text-orange-600"
          aria-label={`Edit ${children}`}
        >
          <CardIcon name="pencil" className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

function EditableField({ label, value, onChange, type = 'text', readOnly = false }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        readOnly={readOnly}
        className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm transition ${
          readOnly
            ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-700'
            : 'border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy'
        }`}
      />
    </div>
  );
}

function InfoValue({ label, value, fallback = 'Not specified' }) {
  const displayValue = value === null || value === undefined || value === '' ? fallback : value;

  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">{label}</p>
      <p className="mt-1 text-sm text-slate-700">{displayValue}</p>
    </div>
  );
}

function normalizeCvResponse(response) {
  let value = response;
  while (value?.data && !value.profile && !value.experience && !value.skills) {
    value = value.data;
  }
  return value || {};
}

function Profile() {
  const { profileStatus, loading, refreshProfile } = useProfileCompletion();
  const { categories } = useCategories();
  const { towns, loading: townsLoading } = useTowns();
  const [summaryEditing, setSummaryEditing] = useState(false);
  const [availabilityEditing, setAvailabilityEditing] = useState(false);
  const [personalModalOpen, setPersonalModalOpen] = useState(false);
  const [skillsModalOpen, setSkillsModalOpen] = useState(false);
  const [skillsStep, setSkillsStep] = useState(1);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [categorySkillOptions, setCategorySkillOptions] = useState([]);
  const [selectedCategorySkills, setSelectedCategorySkills] = useState([]);
  const [skillDetails, setSkillDetails] = useState({});
  const [skillsSubmitting, setSkillsSubmitting] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillSearch, setSkillSearch] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [availabilityDraft, setAvailabilityDraft] = useState('');
  const [savedSummary, setSavedSummary] = useState('');
  const [savedAvailability, setSavedAvailability] = useState('');
  const [formData, setFormData] = useState({});
  const [personalDraft, setPersonalDraft] = useState({});
  const [citySearch, setCitySearch] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoCropFile, setPhotoCropFile] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const [cvOpen, setCvOpen] = useState(false);
  const [cvLoading, setCvLoading] = useState(false);
  const [cvData, setCvData] = useState(null);
  const [showAllWorkExperience, setShowAllWorkExperience] = useState(false);
  const filteredTownOptions = towns.filter((town) => {
    const name = String(town.name || town).toLowerCase();
    const query = (citySearch || personalDraft.city || '').toLowerCase();
    return !query || name.includes(query);
  });

  useEffect(() => {
    if (profileStatus?.user) {
      const responseUser = profileStatus.user;
      const personal = responseUser.personal || responseUser;
      const profile = responseUser.profile || {};
      const professional = responseUser.professional || {};
      const nextFormData = {
        firstName: personal.firstName || '',
        middleName: personal.middleName || '',
        lastName: personal.lastName || '',
        email: personal.email || '',
        phone: personal.phone || '',
        secondaryPhone: personal.secondaryPhone || '',
        isWhatsapp: Boolean(personal.isWhatsapp),
        gender: personal.gender || '',
        dateOfBirth: personal.dateOfBirth || '',
        nationalId: personal.nationalId || '',
        city: personal.location?.city || personal.city || '',
        area: personal.location?.area || personal.area || '',
        bio: profile.bio || professional?.bio || '',
        availability: professional?.availability?.type || profile.availability || '',
        profilePhoto: personal.profilePhoto || '',
      };

      setFormData(nextFormData);
      setPersonalDraft(nextFormData);
      setCitySearch(nextFormData.city || '');
      setSummaryDraft(profile.bio || professional?.bio || '');
      setAvailabilityDraft(professional?.availability?.type || profile.availability || '');
      setSavedSummary(profile.bio || professional?.bio || '');
      setSavedAvailability(professional?.availability?.type || profile.availability || '');
      setSelectedSkills(professional?.skills || []);
    }
  }, [profileStatus]);

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setPhotoError('Please choose an image file.');
      return;
    }

    setPhotoError('');
    setPhotoCropFile(file);
  };

  const handlePhotoCrop = async (file) => {
    setPhotoUploading(true);
    try {
      const uploaded = await uploadProfilePhoto(file);
      const photoUrl = uploaded?.profilePhoto || uploaded?.profile?.profilePhoto || uploaded?.user?.profilePhoto;
      if (!photoUrl) throw new Error('Photo uploaded, but no image URL was returned.');
      setFormData((previous) => ({ ...previous, profilePhoto: photoUrl }));
      setPersonalDraft((previous) => ({ ...previous, profilePhoto: photoUrl }));
      setPhotoCropFile(null);
    } catch (error) {
      setPhotoError(error.message);
      throw error;
    } finally {
      setPhotoUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-3xl bg-white p-6 shadow-sm sm:p-8">
          <p className="text-center text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  const responseUser = profileStatus?.user || {};
  const personal = responseUser.personal || responseUser;
  const responseProfile = responseUser.profile || {};
  const professional = responseUser.professional || {};
  const statistics = responseUser.tumaJobs?.statistics || {};
  const user = {
    ...personal,
    ...formData,
    city: formData.city || personal.location?.city || personal.city,
    area: formData.area || personal.location?.area || personal.area,
    profilePhoto: formData.profilePhoto || personal.profilePhoto,
    skills: professional?.skills || [],
    experience: professional?.experience || [],
    completedJobs: statistics.jobsCompleted || 0,
    rating: statistics.averageRating,
    isVerified: responseUser.verification?.identity?.verified || responseUser.account?.isVerified,
  };
  const profile = { ...responseProfile, ...formData };
  const fullName = [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ');
  const availability = (savedAvailability || profile.availability)?.replace('_', '-') || 'Not specified';
  const profilePhoto = getMediaUrl(profile.profilePhoto || user.profilePhoto);
  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U';

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
      : 'Date unavailable';

  const getExperienceLabel = (experienceStartDate) => {
    if (!experienceStartDate) return '0 years';

    const start = new Date(experienceStartDate);
    if (Number.isNaN(start.getTime())) return '0 years';

    const now = new Date();
    let years = now.getFullYear() - start.getFullYear();
    const monthDiff = now.getMonth() - start.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < start.getDate())) {
      years -= 1;
    }

    if (years <= 0) {
      return 'Less than 1 year';
    }

    return `${years} year${years === 1 ? '' : 's'}`;
  };

  const openSkillsEditor = () => {
    setSkillsModalOpen(true);
    setSkillsStep(1);
    setSelectedCategoryId('');
    setCategorySkillOptions([]);
    setSelectedCategorySkills([]);
    setSkillDetails({});
    setSkillSearch('');
  };

  const closeSkillsEditor = () => {
    setSkillsModalOpen(false);
    setSkillsStep(1);
    setSelectedCategoryId('');
    setCategorySkillOptions([]);
    setSelectedCategorySkills([]);
    setSkillDetails({});
    setSkillSearch('');
  };

  const handleSelectCategory = async (category) => {
    const slug = category.slug || category.id;
    setSelectedCategoryId(slug);
    setSkillsLoading(true);
    try {
      const skills = await fetchSkillsByCategorySlug(slug);
      setCategorySkillOptions(Array.isArray(skills) ? skills : skills?.data || []);
      setSelectedCategorySkills([]);
      setSkillDetails({});
      setSkillsStep(2);
    } catch (error) {
      setCategorySkillOptions([]);
      setSelectedCategorySkills([]);
      setSkillsStep(2);
    } finally {
      setSkillsLoading(false);
    }
  };

  const filteredSkillOptions = categorySkillOptions.filter((skill) =>
    skill.name.toLowerCase().includes(skillSearch.toLowerCase()),
  );

  const toggleSelectedCategorySkill = (skill) => {
    setSelectedCategorySkills((current) => {
      const exists = current.some((item) => item.id === skill.id);
      if (exists) {
        return current.filter((item) => item.id !== skill.id);
      }
      return [...current, skill];
    });
  };

  const continueToSkillDetails = () => {
    const chosenSkills = selectedCategorySkills.map((skill) => ({
      ...skill,
      skillId: skill.id,
      experienceStartDate: skill.experienceStartDate || '',
      hasCertificate: Boolean(skill.hasCertificate),
      certificateName: skill.certificateName || '',
      isPrimary: Boolean(skill.isPrimary),
    }));

    const nextSkillDetails = {};
    chosenSkills.forEach((skill) => {
      nextSkillDetails[skill.id] = {
        skillId: skill.id,
        name: skill.name,
        experienceStartDate: skill.experienceStartDate || '',
        hasCertificate: Boolean(skill.hasCertificate),
        certificateName: skill.certificateName || '',
        isPrimary: Boolean(skill.isPrimary),
      };
    });

    setSkillDetails(nextSkillDetails);
    setSkillsStep(3);
  };

  const handleSkillDetailChange = (skillId, field, value) => {
    setSkillDetails((current) => ({
      ...current,
      [skillId]: {
        ...(current[skillId] || { skillId }),
        [field]: value,
      },
    }));
  };

  const handleSaveSkills = async () => {
    const entries = Object.values(skillDetails);
    if (!entries.length) {
      closeSkillsEditor();
      return;
    }

    setSkillsSubmitting(true);
    try {
      const payloads = entries.map((entry) => ({
        skillId: entry.skillId,
        experienceStartDate: entry.experienceStartDate || '',
        hasCertificate: Boolean(entry.hasCertificate),
        certificateName: entry.hasCertificate ? (entry.certificateName || '') : '',
        isPrimary: Boolean(entry.isPrimary),
      }));

      for (const payload of payloads) {
        await addUserSkill(payload);
      }

      const mergedSkills = [...selectedSkills];
      entries.forEach((entry) => {
        const skillName = categorySkillOptions.find((skill) => skill.id === entry.skillId)?.name || entry.name || 'Skill';
        if (!mergedSkills.some((skill) => skill.id === entry.skillId)) {
          mergedSkills.push({
            id: entry.skillId,
            name: skillName,
            experienceStartDate: entry.experienceStartDate || '',
            hasCertificate: Boolean(entry.hasCertificate),
            certificateName: entry.certificateName || '',
            isPrimary: Boolean(entry.isPrimary),
          });
        }
      });

      setSelectedSkills(mergedSkills);
      await refreshProfile();
      closeSkillsEditor();
    } catch (error) {
      console.error('Failed to save skills', error);
    } finally {
      setSkillsSubmitting(false);
    }
  };

  const handleRemoveSkill = async (skillId) => {
    try {
      await removeUserSkill(skillId);
      setSelectedSkills((current) => current.filter((skill) => skill.id !== skillId));
      await refreshProfile();
    } catch (error) {
      console.error('Failed to remove skill', error);
    }
  };

  const openMyCv = async () => {
    const userId = profileStatus?.user?.id || responseUser?.id || personal?.id || personal?.userId;
    if (!userId) return;

    setCvLoading(true);
    try {
      const data = await fetchUserCv(userId);
      setCvData(normalizeCvResponse(data));
      setCvOpen(true);
    } catch (error) {
      console.error('Failed to load worker CV', error);
    } finally {
      setCvLoading(false);
    }
  };

  const openPersonalEditor = () => {
    setPersonalDraft({ ...formData });
    setCitySearch(formData.city || '');
    setShowCitySuggestions(false);
    setPersonalModalOpen(true);
  };

  const closePersonalEditor = () => {
    setPersonalDraft({ ...formData });
    setCitySearch(formData.city || '');
    setShowCitySuggestions(false);
    setPersonalModalOpen(false);
  };

  const savePersonalInfo = async () => {
    const nextPersonalDraft = {
      ...personalDraft,
      nationalId: formatNationalId(personalDraft.nationalId),
    };

    const updates = {};
    Object.keys(nextPersonalDraft).forEach((field) => {
      if (nextPersonalDraft[field] !== formData[field]) {
        updates[field] = nextPersonalDraft[field];
      }
    });

    if (Object.keys(updates).length > 0) {
      await updateProfile(updates);
      await refreshProfile();
      setFormData((previous) => ({ ...previous, ...updates }));
    }

    setPersonalModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-2xl font-bold text-white ring-4 ring-orange-50">
            {profilePhoto ? (
              <img
                src={profilePhoto}
                alt={`${fullName} profile`}
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              initials
            )}
          </div>
          <h1 className="mt-5 text-2xl font-bold uppercase tracking-tight text-navy sm:text-3xl">
            {fullName || 'Worker Profile'}
          </h1>
          <p className="mt-2 text-sm font-semibold text-slate-600">
            {responseUser.account?.roleLabel || professional?.title || 'Find Work'}
          </p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600">
            <CardIcon name="location" />{user.area ? `${user.area}, ` : ''}
            {user.city || 'Location unavailable'}
          </p>
          {user.phone && <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="phone" />{user.phone}</p>}
          {user.email && <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="mail" />{user.email}</p>}
          {user.isWhatsapp && <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CardIcon name="message" />WhatsApp</p>}
          <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700">
            <CardIcon name="check" />Available {availability}
          </p>

          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={openMyCv}
              disabled={cvLoading}
              className="rounded-2xl bg-navy px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-60"
            >
              {cvLoading ? 'Loading CV...' : 'View CV'}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <SectionHeading icon="user" onEdit={openPersonalEditor}>Personal Information</SectionHeading>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <InfoValue label="Full name" value={fullName || 'Not specified'} />
          <InfoValue label="Phone" value={user.phone} />
          <InfoValue label="Secondary phone" value={user.secondaryPhone} />
          <InfoValue label="Email" value={user.email} />
          <InfoValue label="WhatsApp" value={user.isWhatsapp ? 'Yes' : 'No'} />
          <InfoValue label="Gender" value={user.gender} />
          <InfoValue label="Date of birth" value={user.dateOfBirth ? formatDate(user.dateOfBirth) : 'Not specified'} />
          <InfoValue label="National ID" value={user.nationalId} />
          <InfoValue label="City" value={user.city} />
          <InfoValue label="Area" value={user.area} />
          <InfoValue label="Profile photo" value={profilePhoto ? 'Available' : 'Not uploaded'} />
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <SectionHeading icon="application" onEdit={() => { setSummaryDraft(savedSummary); setSummaryEditing(true); }}>
          Professional Summary
        </SectionHeading>
        {summaryEditing ? (
          <div className="mt-4">
            <textarea
              value={summaryDraft}
              onChange={(event) => setSummaryDraft(event.target.value.slice(0, 500))}
              maxLength="500"
              rows="4"
              className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
              placeholder="Tell employers about your experience and services"
            />
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
              <span>{summaryDraft.length} / 500 characters</span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSummaryDraft(savedSummary);
                    setSummaryEditing(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={async () => {
                    const nextSummary = summaryDraft.trim();
                    if (nextSummary === savedSummary) {
                      setSummaryEditing(false);
                      return;
                    }

                    await updateProfile({ bio: nextSummary });
                    await refreshProfile();
                    setSavedSummary(nextSummary);
                    setSummaryEditing(false);
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm leading-7 text-slate-700">
            {savedSummary || 'Add a professional summary to help employers understand your experience.'}
          </p>
        )}
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name="category" />Skills & Services</h2>
          <button
            type="button"
            onClick={openSkillsEditor}
            className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm font-bold text-orange-700 transition hover:bg-orange-100"
          >
            + Add
          </button>
        </div>

        <div className="mt-5 space-y-4">
          {selectedSkills.length > 0 ? (
            selectedSkills.map((skill) => (
              <div key={skill.id} className="group relative rounded-2xl border border-slate-200 bg-slate-50 p-4 transition hover:border-orange-200 hover:bg-orange-50">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                      <CardIcon name="starFilled" className="h-4 w-4 text-orange-600" />
                      {skill.name}
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      {getExperienceLabel(skill.experienceStartDate)} experience
                    </p>
                    <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-600">
                      {skill.hasCertificate ? <><CardIcon name="graduation" />{skill.certificateName || 'Certificate'}</> : 'No certificate'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveSkill(skill.id)}
                    className="mt-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-500 opacity-0 transition group-hover:opacity-100 hover:border-rose-200 hover:text-rose-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500">No skills added yet. Add skills to help employers find you.</p>
          )}
        </div>
      </div>

      {skillsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-xl font-bold uppercase tracking-tight text-navy">Add Skills</h2>
              <button
                type="button"
                onClick={closeSkillsEditor}
                className="text-2xl leading-none text-slate-400 transition hover:text-slate-700"
                aria-label="Close skill editor"
              >
                ×
              </button>
            </div>

            {skillsStep === 1 && (
              <div className="mt-6">
                <p className="text-sm font-semibold uppercase tracking-wide text-slate-600">Choose a category</p>
                <div className="mt-4 space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id || category.slug}
                      type="button"
                      onClick={() => handleSelectCategory(category)}
                      className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 transition hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                    >
                      <span className="inline-flex items-center gap-2"><CardIcon name="category" />{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {skillsStep === 2 && (
              <div className="mt-6">
                <p className="text-sm font-semibold uppercase tracking-wide text-slate-600">{selectedCategoryId ? 'Select the skills you can do' : 'Skills'}</p>
                <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold text-slate-800">
                  {categories.find((category) => (category.slug || category.id) === selectedCategoryId)?.name || 'Selected category'}
                </div>

                <div className="mt-4 space-y-2">
                  {skillsLoading && <p className="text-sm text-slate-500">Loading skills...</p>}
                  {!skillsLoading && filteredSkillOptions.length === 0 && (
                    <p className="text-sm text-slate-500">No skills available in this category.</p>
                  )}
                  {!skillsLoading && filteredSkillOptions.map((skill) => {
                    const active = selectedCategorySkills.some((item) => item.id === skill.id);
                    return (
                      <label
                        key={skill.id}
                        className="flex cursor-pointer items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 transition hover:border-orange-200 hover:bg-orange-50"
                      >
                        <span className="inline-flex items-center gap-2 font-medium"><CardIcon name={active ? 'check' : 'circle'} />{skill.name}</span>
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleSelectedCategorySkill(skill)}
                          className="hidden"
                        />
                      </label>
                    );
                  })}
                </div>

                <div className="mt-5 flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-600">Selected: {selectedCategorySkills.length}</p>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setSkillsStep(1)}>Back</Button>
                    <Button variant="primary" onClick={continueToSkillDetails} disabled={selectedCategorySkills.length === 0}>
                      Continue →
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {skillsStep === 3 && (
              <div className="mt-6">
                <p className="text-sm font-semibold uppercase tracking-wide text-slate-600">Complete your skill details</p>
                <div className="mt-4 space-y-4">
                  {selectedCategorySkills.map((skill) => {
                    const skillDetail = skillDetails[skill.id] || {
                      skillId: skill.id,
                      name: skill.name,
                      experienceStartDate: '',
                      hasCertificate: false,
                      certificateName: '',
                      isPrimary: false,
                    };

                    return (
                      <div key={skill.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900">
                          <CardIcon name="starFilled" className="h-4 w-4 text-orange-600" />
                          {skill.name}
                        </p>

                        <div className="space-y-3">
                          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Started doing this work
                          </label>
                          <div className="relative">
                            <input
                              type="date"
                              value={skillDetail.experienceStartDate || ''}
                              onChange={(event) => handleSkillDetailChange(skill.id, 'experienceStartDate', event.target.value)}
                              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                            />
                          </div>

                          <div className="text-sm text-slate-600">
                            Experience: {getExperienceLabel(skillDetail.experienceStartDate)}
                          </div>

                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Certificate</p>
                            <div className="mt-2 flex gap-3">
                              <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                  type="radio"
                                  name={`certificate-${skill.id}`}
                                  checked={Boolean(skillDetail.hasCertificate)}
                                  onChange={() => handleSkillDetailChange(skill.id, 'hasCertificate', true)}
                                />
                                Yes
                              </label>
                              <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                  type="radio"
                                  name={`certificate-${skill.id}`}
                                  checked={!Boolean(skillDetail.hasCertificate)}
                                  onChange={() => {
                                    handleSkillDetailChange(skill.id, 'hasCertificate', false);
                                    handleSkillDetailChange(skill.id, 'certificateName', '');
                                  }}
                                />
                                No
                              </label>
                            </div>
                          </div>

                          {Boolean(skillDetail.hasCertificate) && (
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Certificate name
                              </label>
                              <input
                                type="text"
                                value={skillDetail.certificateName || ''}
                                onChange={(event) => handleSkillDetailChange(skill.id, 'certificateName', event.target.value)}
                                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                                placeholder="Certificate name"
                              />
                            </div>
                          )}

                          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                            <input
                              type="checkbox"
                              checked={Boolean(skillDetail.isPrimary)}
                              onChange={(event) => {
                                const nextValue = event.target.checked;
                                setSkillDetails((current) => {
                                  const next = { ...current };
                                  Object.keys(next).forEach((key) => {
                                    if (key !== skill.id) {
                                      next[key] = { ...next[key], isPrimary: false };
                                    }
                                  });
                                  next[skill.id] = { ...(next[skill.id] || { skillId: skill.id, name: skill.name }), isPrimary: nextValue };
                                  return next;
                                });
                              }}
                            />
                            Primary skill
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-5 flex items-center justify-end gap-2">
                  <Button variant="ghost" onClick={() => setSkillsStep(2)}>← Back</Button>
                  <Button variant="primary" onClick={handleSaveSkills} disabled={skillsSubmitting}>
                    {skillsSubmitting ? 'Saving...' : `Add ${selectedCategorySkills.length} Skills`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name="briefcase" />Work Experience</h2>
        {user.experience && user.experience.length > 0 ? (
          <div className="mt-4 space-y-4">
            {(showAllWorkExperience ? user.experience : user.experience.slice(0, 3)).map((exp) => (
              <div key={exp.id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                <h3 className="font-semibold text-slate-900">{exp.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{exp.employerName}</p>
                <p className="mt-1 text-sm text-slate-500">{formatDate(exp.startDate)}</p>
                <p className="mt-2 text-sm text-slate-600">{exp.description}</p>
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CardIcon name="check" />Completed</p>
                {exp.rating && <p className="mt-1 text-xs text-slate-500">Rating: {exp.rating}</p>}
              </div>
            ))}

            {user.experience.length > 3 && (
              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={() => setShowAllWorkExperience((current) => !current)}
                  className="text-sm font-semibold text-orange-700 hover:text-orange-800"
                >
                  {showAllWorkExperience ? 'Show less experience' : 'View More Experience →'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="mt-4 text-sm text-slate-500">No work experience added yet.</p>
        )}
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name="briefcase" />Tuma Jobs Experience</h2>
        <div className="mt-4 flex items-end gap-3">
          <p className="text-4xl font-bold text-orange-600">{user.completedJobs || 0}</p>
          <p className="pb-1 text-sm font-semibold text-slate-600">Jobs Completed</p>
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name="star" />Rating</h2>
        <p className="mt-4 text-sm text-slate-600">
          {user.rating || user.averageRating
            ? <span className="inline-flex items-center gap-1.5"><CardIcon name="starFilled" className="h-4 w-4 text-orange-500" />{user.rating || user.averageRating}</span>
            : 'No ratings yet. Ratings will appear after completed jobs.'}
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="rounded-3xl bg-white p-6 shadow-sm">
          <SectionHeading icon="check" onEdit={() => { setAvailabilityDraft(savedAvailability); setAvailabilityEditing(true); }}>
            Availability
          </SectionHeading>
          {availabilityEditing ? (
            <div className="mt-4 space-y-3">
              {[
                ['FULL_TIME', 'Full-Time'],
                ['PART_TIME', 'Part-Time'],
                ['CONTRACT', 'Contract'],
                ['ANYTIME', 'Anytime'],
              ].map(([value, label]) => (
                <label key={value} className="flex items-center gap-3 text-sm text-slate-700">
                  <input
                    type="radio"
                    name="availability"
                    value={value}
                    checked={availabilityDraft === value}
                    onChange={(event) => setAvailabilityDraft(event.target.value)}
                  />
                  {label}
                </label>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setAvailabilityDraft(savedAvailability);
                    setAvailabilityEditing(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={async () => {
                    if (availabilityDraft !== savedAvailability) {
                      await updateProfile({ availability: availabilityDraft });
                      await refreshProfile();
                      setSavedAvailability(availabilityDraft);
                    }
                    setAvailabilityEditing(false);
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CardIcon name="check" />{availability}</p>
          )}
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name="shield" />Verification</h2>
          <div className="mt-4 space-y-2 text-sm">
            {!user.isVerified && <PhoneVerification phone={user.phone} onAddPhone={openPersonalEditor} onVerified={refreshProfile} />}
            <p className={`inline-flex items-center gap-1.5 ${user.isWhatsapp ? 'text-emerald-700' : 'text-slate-500'}`}>
              <CardIcon name={user.isWhatsapp ? 'check' : 'circle'} />WhatsApp Available
            </p>
            <p className={`inline-flex items-center gap-1.5 ${user.isVerified ? 'text-emerald-700' : 'text-slate-500'}`}>
              <CardIcon name={user.isVerified ? 'shield' : 'circle'} />{user.isVerified ? 'Identity Verified' : 'Identity Not Yet Verified'}
            </p>
          </div>
        </div>
      </div>

      {cvOpen && cvData && (
        <CvModal
          cv={cvData}
          applicant={{ applicant: { ...personal, ...formData } }}
          job={{ title: 'Applicant profile', skill: { name: professional?.title || 'Applicant profile' } }}
          onClose={() => setCvOpen(false)}
        />
      )}

      {photoCropFile && <ProfilePhotoCropper file={photoCropFile} onCancel={() => { setPhotoCropFile(null); setPhotoError(''); }} onCrop={handlePhotoCrop} isProcessing={photoUploading} error={photoError} />}

      {personalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-xl font-bold uppercase tracking-tight text-navy">Edit Personal Information</h2>
              <button
                type="button"
                onClick={closePersonalEditor}
                className="text-2xl leading-none text-slate-400 transition hover:text-slate-700"
                aria-label="Close personal info editor"
              >
                ×
              </button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <EditableField
                label="First Name"
                value={personalDraft.firstName}
                onChange={(value) => setPersonalDraft((previous) => ({ ...previous, firstName: value }))}
              />
              <EditableField
                label="Middle Name"
                value={personalDraft.middleName}
                onChange={(value) => setPersonalDraft((previous) => ({ ...previous, middleName: value }))}
              />
              <EditableField
                label="Last Name"
                value={personalDraft.lastName}
                onChange={(value) => setPersonalDraft((previous) => ({ ...previous, lastName: value }))}
              />
              <EditableField
                label="Email"
                type="email"
                value={personalDraft.email}
                onChange={(value) => setPersonalDraft((previous) => ({ ...previous, email: value }))}
              />
              <EditableField
                label="Phone"
                type="tel"
                value={personalDraft.phone}
                onChange={(value) => setPersonalDraft((previous) => ({ ...previous, phone: value }))}
              />
              <EditableField
                label="Secondary Phone"
                type="tel"
                value={personalDraft.secondaryPhone}
                onChange={(value) => setPersonalDraft((previous) => ({ ...previous, secondaryPhone: value }))}
              />
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">Gender</label>
                <select
                  value={personalDraft.gender || ''}
                  onChange={(event) => setPersonalDraft((previous) => ({ ...previous, gender: event.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                >
                  <option value="">Select gender</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                </select>
              </div>
              <EditableField
                label="Date of Birth"
                type="date"
                value={personalDraft.dateOfBirth}
                onChange={(value) => setPersonalDraft((previous) => ({ ...previous, dateOfBirth: value }))}
              />
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">National ID</label>
                <input
                  type="text"
                  value={personalDraft.nationalId || ''}
                  onChange={(event) =>
                    setPersonalDraft((previous) => ({
                      ...previous,
                      nationalId: formatNationalId(event.target.value),
                    }))
                  }
                  placeholder="123456/78/9"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">City</label>
                <div className="relative">
                  <input
                    type="text"
                    value={personalDraft.city || citySearch}
                    onFocus={() => setShowCitySuggestions(true)}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setCitySearch(nextValue);
                      setPersonalDraft((previous) => ({ ...previous, city: nextValue }));
                      setShowCitySuggestions(true);
                    }}
                    placeholder="Search town or city"
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                  />
                  {showCitySuggestions && (citySearch || personalDraft.city) && (
                    <div className="absolute z-10 mt-2 max-h-48 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1 shadow-lg">
                      {townsLoading && <p className="px-3 py-2 text-sm text-slate-500">Loading towns...</p>}
                      {!townsLoading && filteredTownOptions.length > 0 ? (
                        filteredTownOptions.map((town) => {
                          const townName = town.name || town;
                          return (
                            <button
                              key={town.id || townName}
                              type="button"
                              onClick={() => {
                                setCitySearch(townName);
                                setPersonalDraft((previous) => ({ ...previous, city: townName }));
                                setShowCitySuggestions(false);
                              }}
                              className="block w-full rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-orange-50 hover:text-slate-900"
                            >
                              {townName}
                            </button>
                          );
                        })
                      ) : (
                        <p className="px-3 py-2 text-sm text-slate-500">No matching towns</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <EditableField
                label="Area"
                value={personalDraft.area}
                onChange={(value) => setPersonalDraft((previous) => ({ ...previous, area: value }))}
              />
              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(personalDraft.isWhatsapp)}
                  onChange={(event) =>
                    setPersonalDraft((previous) => ({ ...previous, isWhatsapp: event.target.checked }))
                  }
                />
                WhatsApp available
              </label>
              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Profile Photo
                </label>
                <div className="flex items-center gap-3">
                  <input type="file" accept="image/*" onChange={handlePhotoChange} className="block w-full text-sm text-slate-600" />
                  {photoUploading && <span className="text-sm text-slate-500">Uploading...</span>}
                </div>
                {photoError && <p className="mt-2 text-sm text-rose-600">{photoError}</p>}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={closePersonalEditor}>Cancel</Button>
              <Button variant="primary" onClick={savePersonalInfo}>Save Changes</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;

